/**
 * Top-level router (createBrowserRouter, data-router / library mode).
 *
 * Public auth routes are outside <AuthGate>; everything else is gated behind
 * <AuthGate> → <AppLayout>. Every section route renders its real screen
 * (TMMPDM-0019 replaced the last SectionPlaceholder, Add-on catalog),
 * each keeping its path and heading key.
 */
import { Navigate, createBrowserRouter, type RouteObject } from 'react-router-dom';
import { RootLayout } from '@/routes/RootLayout';
import { AppLayout } from '@/components/AppLayout';
import { AuthGate } from '@/auth/AuthGate';
import { AuthReturn } from '@/routes/AuthReturn';
import { NoAccess } from '@/routes/NoAccess';
import { SignedOut } from '@/routes/SignedOut';
import { NotFound } from '@/routes/NotFound';
import { RouteError } from '@/routes/RouteError';
import { AddOns } from '@/routes/AddOns';
import { Dashboard } from '@/routes/Dashboard';
import { PartnerAffiliatePage } from '@/routes/partners/PartnerAffiliatePage';
import { Partners } from '@/routes/partners/Partners';
import { PartnerPage } from '@/routes/partners/PartnerPage';
import {
  LEGACY_ROUTES,
  PartnerListRedirect,
  ResellerRedirect,
  TenantRedirect,
} from '@/routes/partners/PartnerRedirects';
import { LegacyResellerCardRedirect } from '@/routes/partners/LegacyResellerCardRedirect';
import { RateCardsPage } from '@/routes/rateCards/RateCardsPage';
import { RateCardPage } from '@/routes/rateCards/RateCardPage';
import {
  LegacyCustomerCardRedirect,
  LegacyWholesaleCardRedirect,
} from '@/routes/rateCards/LegacyRateCardRedirects';
import {
  CommissionPlanEditPage,
  CommissionPlanNewPage,
} from '@/routes/resellers/CommissionPlanEditPage';
import { NewAgreementPage } from '@/routes/resellers/NewAgreementPage';
import { Revenue } from '@/routes/Revenue';
import { Payouts } from '@/routes/Payouts';
import { ParkedEvents } from '@/routes/billing/ParkedEvents';
import { AiCostReconciliationPage } from '@/routes/ai/AiCostReconciliationPage';
import { AiModelPrices } from '@/routes/AiModelPrices';
import { BillingAttention } from '@/routes/BillingAttention';
import { ProductSettings } from '@/routes/ProductSettings';
import { TicketsPage } from '@/routes/tickets/TicketsPage';
import { TicketPage } from '@/routes/tickets/TicketPage';
import { AnnouncementEditPage } from '@/routes/tickets/AnnouncementEditPage';
import { MerchantKnowledgePage } from '@/routes/knowledge/MerchantKnowledgePage';

/** Exported as a test seam so `router.test.tsx` and
 * `placeholderCoverage.test.tsx` mount the same table through
 * `createMemoryRouter` — same pattern as `authStore.__resetAuthStore` and
 * `bootstrap.__resetBootstrap`. Not consumed anywhere else. */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      // Public auth routes — no AuthGate.
      { path: 'auth/return', element: <AuthReturn /> },
      { path: 'auth/no-access', element: <NoAccess /> },
      { path: 'auth/signed-out', element: <SignedOut /> },

      // Everything else is gated behind AuthGate → AppLayout.
      {
        element: <AuthGate />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Navigate to="/dashboard" replace /> },
              { path: 'dashboard', element: <Dashboard /> },
              { path: LEGACY_ROUTES.tenantList, element: <PartnerListRedirect /> },
              { path: LEGACY_ROUTES.resellerList, element: <PartnerListRedirect /> },
              { path: LEGACY_ROUTES.tenant, element: <TenantRedirect /> },
              { path: LEGACY_ROUTES.reseller, element: <ResellerRedirect /> },
              { path: 'partners', element: <Partners /> },
              // TMMPDM-0109: support cases and feature requests; a ticket by its number (SC-1001).
              { path: 'tickets', element: <TicketsPage /> },
              { path: 'tickets/:number', element: <TicketPage /> },
              // TMMPDM-0110: an announcement ("new", or its id) under Support's Announcements tab.
              { path: 'tickets/announcements/:id', element: <AnnouncementEditPage /> },
              // TMMPDM-0112: merchant patterns and unlisted banks, one tab each.
              { path: 'merchant-knowledge', element: <MerchantKnowledgePage /> },
              { path: 'partners/by-tenant/:slug/*', element: <TenantRedirect canonical /> },
              { path: 'partners/:resellerId', element: <PartnerPage /> },
              {
                path: 'partners/:resellerId/affiliates/:affiliateId',
                element: <PartnerAffiliatePage />,
              },
              // TMMPDM-0086: the reseller-card editor is gone. Old URLs replace to Pricing → Rate card.
              {
                path: 'partners/:resellerId/rate-cards/new',
                element: <LegacyResellerCardRedirect />,
              },
              {
                path: 'partners/:resellerId/rate-cards/:cardId/edit',
                element: <LegacyResellerCardRedirect />,
              },
              // TMMPDM-0041: a commission plan's editor, under its reseller.
              {
                path: 'partners/:resellerId/commission-plans/new',
                element: <CommissionPlanNewPage />,
              },
              {
                path: 'partners/:resellerId/commission-plans/:planId/edit',
                element: <CommissionPlanEditPage />,
              },
              { path: 'partners/:resellerId/agreement/new', element: <NewAgreementPage /> },
              // TMMPDM-0089: one Rate cards list and card page. The customer card URLs and the
              // retired wholesale editor's replace to them.
              { path: 'rate-cards', element: <RateCardsPage /> },
              { path: 'rate-cards/new', element: <LegacyWholesaleCardRedirect /> },
              { path: 'rate-cards/:id', element: <RateCardPage /> },
              { path: 'rate-cards/:id/edit', element: <LegacyWholesaleCardRedirect /> },
              { path: 'customer-rate-cards', element: <LegacyCustomerCardRedirect /> },
              { path: 'customer-rate-cards/:id', element: <LegacyCustomerCardRedirect /> },
              { path: 'add-ons', element: <AddOns /> },
              { path: 'revenue', element: <Revenue /> },
              { path: 'payouts', element: <Payouts /> },
              { path: 'billing/parked-events', element: <ParkedEvents /> },
              { path: 'billing-attention', element: <BillingAttention /> },
              { path: 'ai-cost-reconciliation', element: <AiCostReconciliationPage /> },
              { path: 'ai-model-prices', element: <AiModelPrices /> },
              { path: 'product-settings', element: <ProductSettings /> },

              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
